/**
 * First-touch attribution capture.
 *
 * Reads UTM params + referrer + landing path from the browser on first visit
 * to /signup or /login, persists to localStorage (keyed), then ships the
 * payload to /api/onboarding/complete which writes it to
 * merchants.acquisition_source.
 *
 * "First-touch" wins: we only write to localStorage if no prior record exists.
 * OAuth round-trips clobber the URL, so re-running on later visits must not
 * overwrite the genuine first touch.
 */

const STORAGE_KEY = 'novix_attribution'

export interface Attribution {
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_content?: string
  utm_term?: string
  referrer?: string
  landing_page?: string
  first_touch_at: string
}

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const

/**
 * Capture attribution if the current URL carries UTM params or has an external
 * referrer. Idempotent: does nothing if a record already exists. Safe to call
 * from any client page's useEffect.
 */
export function captureAttribution(): void {
  if (typeof window === 'undefined') return
  try {
    if (localStorage.getItem(STORAGE_KEY)) return // already captured — first-touch wins

    const params = new URLSearchParams(window.location.search)
    const attribution: Attribution = { first_touch_at: new Date().toISOString() }

    let hasSignal = false
    for (const k of UTM_KEYS) {
      const v = params.get(k)
      if (v) {
        attribution[k] = v
        hasSignal = true
      }
    }

    const referrer = document.referrer
    if (referrer && !referrer.startsWith(window.location.origin)) {
      attribution.referrer = referrer
      hasSignal = true
    }

    if (hasSignal) {
      attribution.landing_page = window.location.pathname
      localStorage.setItem(STORAGE_KEY, JSON.stringify(attribution))
    }
  } catch {
    // Private-mode / storage-quota failures are non-critical; swallow.
  }
}

export function readAttribution(): Attribution | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? (JSON.parse(raw) as Attribution) : null
  } catch {
    return null
  }
}

export function clearAttribution(): void {
  if (typeof window === 'undefined') return
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    // noop
  }
}
